import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowUp,
  ChartBar,
  ChatCircle,
  Gear,
  Globe,
  ListChecks,
  Car,
  Robot,
  TelegramLogo,
  WhatsappLogo,
} from '@phosphor-icons/react';
import { chatMirror } from '../../services/chatMirror';
import { useTranslation } from '../../i18n/I18nContext';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MockWindow } from './MockWindow';
import './PanelInbox.css';

const CHANNEL_ICON = {
  website: Globe,
  whatsapp: WhatsappLogo,
  telegram: TelegramLogo,
};

/**
 * Разговоры в инбоксе. Люди те же, что в живом чате слева и в фикстурах
 * демо-панели: тур должен читаться как один рабочий день дилера, а не как
 * набор несвязанных выдумок.
 */
const CONVERSATIONS = [
  {
    id: 'maria',
    name: 'Maria Georgiou',
    channel: 'telegram',
    time: '15:11',
    unread: 1,
    preview: 'Saturday morning works for me',
    previewKey: 'maria.m5',
    thread: [
      { from: 'customer', text: 'Hi, is the Tiguan still available?', key: 'maria.m1' },
      {
        from: 'agent',
        text: 'Yes, it is. 2022, 22,250 km, €45,990. Would you like to see it?',
        key: 'maria.m2',
      },
      { from: 'customer', text: 'Can I book a test drive?', key: 'maria.m3' },
      {
        from: 'agent',
        text: 'Of course. We have slots on Saturday morning and Monday afternoon.',
        key: 'maria.m4',
      },
      { from: 'customer', text: 'Saturday morning works for me', key: 'maria.m5' },
    ],
  },
  {
    id: 'andreas',
    name: 'Andreas Nicolaou',
    channel: 'whatsapp',
    time: '14:26',
    unread: 0,
    preview: 'Let me check with the manager and come back to you',
    previewKey: 'andreas.m2',
    thread: [
      { from: 'customer', text: 'Is the price on the BMW negotiable?', key: 'andreas.m1' },
      {
        from: 'human',
        text: 'Let me check with the manager and come back to you',
        key: 'andreas.m2',
      },
    ],
  },
  {
    id: 'elena',
    name: 'Elena Petrova',
    channel: 'website',
    time: '12:58',
    unread: 1,
    preview: 'What would you offer for my 2018 Golf?',
    previewKey: 'elena.m3',
    thread: [
      { from: 'customer', text: 'Do you take trade-ins?', key: 'elena.m1' },
      { from: 'agent', text: 'We do. What are you driving at the moment?', key: 'elena.m2' },
      { from: 'customer', text: 'What would you offer for my 2018 Golf?', key: 'elena.m3' },
    ],
  },
];

/**
 * Разделы сайдбара — те же и в том же порядке, что в настоящей панели
 * (`app.routes.ts`): Leads, Inventory, Conversation Logs, Chats, Analytics,
 * Settings. Открыт Chats — этот экран мы и показываем.
 */
const NAV = [
  { icon: ListChecks, label: 'Leads', key: 'leads' },
  { icon: Car, label: 'Inventory', key: 'inventory' },
  { icon: ChatCircle, label: 'Conversation Logs', key: 'conversationLogs' },
  { icon: ChatCircle, label: 'Chats', key: 'chats', active: true },
  { icon: ChartBar, label: 'Analytics', key: 'analytics' },
  { icon: Gear, label: 'Settings', key: 'settings' },
];

/**
 * Ответ бота помечен значком робота ПОД текстом (#1417). Раньше ассистент и
 * менеджер различались заливкой — насыщенный синий против почти чёрного, — и
 * подписью над пузырём; в самой панели (b2b-ui `chat-thread`) пузыри светлые,
 * и мокап выглядел другим продуктом. Теперь наши ответы одного светлого тона,
 * а кто ответил, говорит значок: есть робот — бот, нет — человек.
 */
const BOT_AUTHOR = 'agent';

/**
 * PanelInbox — как переписки выглядят на стороне дилера (#1330).
 *
 * ## Зачем блок
 *
 * Живой чат рядом показывает сторону ПОКУПАТЕЛЯ. Ценность продукта в другом:
 * человек пишет откуда ему удобно — Telegram, WhatsApp, сайт, — а команда
 * видит это одним списком в одном месте. Без второго экрана это остаётся
 * словами в подписи.
 *
 * ## Это мокап, а не встроенная панель
 *
 * Настоящий экран живёт в b2b-ui за авторизацией; втащить его сюда нельзя
 * (`X-Frame-Options: SAMEORIGIN`, и он требует сессию). Поэтому здесь
 * воспроизведена его схема — список слева, переписка справа, метка канала у
 * каждой строки, — на фикстурах.
 *
 * Строение повторяет `features/chats/chats.component.html` панели, чтобы
 * витрина не расходилась с продуктом. Данные вымышленные и совпадают с теми,
 * что в моках демо-панели: один и тот же рабочий день.
 *
 * Файл в `pages/shared/`, поэтому в Shadow DOM дилера не инлайнится.
 */
export const PanelInbox = ({ conversations = CONVERSATIONS }) => {
  // Живая строка: то, что посетитель пишет в чат рядом, приходит сюда — как
  // пришло бы команде. Ради этого блок и стоит на странице (#1330).
  // Подписка, а не контекст: ответ агента приходит потоком, и на каждый его
  // кусок перерисовывается только этот мокап (#1330).
  const { t } = useTranslation();
  const live = useSyncExternalStore(
    chatMirror.subscribe,
    chatMirror.getSnapshot
  );

  const rows = useMemo(() => {
    if (!live.length) return conversations;
    const last = live[live.length - 1];
    // Непрочитанным считается написанное покупателем: ответы ассистента
    // команде «читать» не нужно — он уже ответил.
    const unread = live.filter((m) => m.from === 'customer').length;
    const liveRow = {
      id: 'live',
      name: 'You',
      // Канал у самого сообщения, а не текущий выбор: см. `chatMirror`.
      channel: live[0].channel ?? 'website',
      // 24 часа принудительно: у фикстур время в этом виде, а локаль агента
      // могла бы выдать «10:22 PM» и живая строка выпала бы из ряда.
      time: new Date(last.at).toLocaleTimeString('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
      }),
      unread,
      preview: last.text,
      live: true,
      thread: live.map(({ from, text }) => ({ from, text })),
    };
    return [liveRow, ...conversations];
  }, [live, conversations]);

  const [activeId, setActiveId] = useState(conversations[0]?.id);
  // AI mode — как в панели: пока включён, отвечает ассистент; отжали — за
  // разговор берётся человек. Ради этого тумблер тут и стоит (#1330).
  const [aiMode, setAiMode] = useState(true);
  const [draft, setDraft] = useState('');

  // Новая строка сразу открывается: у команды это и есть работа с входящим,
  // а посетителю надо увидеть, что его сообщение доехало.
  useEffect(() => {
    if (live.length) setActiveId('live');
  }, [live.length]);

  const active = rows.find((c) => c.id === activeId) ?? rows[0];

  return (
    <MockWindow
      className="panel-mock"
      aria-label={t('showcase.inbox.windowLabel', "What the dealer's team sees")}
    >
      <div className="panel-mock__viewport">
        <header className="panel-mock__header" aria-hidden="true">
          <img
            className="panel-mock__logo"
            src="/assets/logo-horizontal-light.svg"
            alt=""
          />
          <span className="panel-mock__user">
            <span className="panel-mock__avatar">DM</span>
            <span className="panel-mock__user-name">Demo Manager</span>
          </span>
        </header>

        <div className="panel-mock__body">
          <nav className="panel-mock__sidebar" aria-hidden="true">
            {NAV.map(({ icon: Icon, label: fallback, key, active }) => {
              // Подписи разделов — на языке витрины, как в самой панели (#1431).
              const label = t(`showcase.inbox.nav.${key}`, fallback);
              return (
              <span
                key={key}
                className={`panel-mock__nav-item${
                  active ? ' panel-mock__nav-item--active' : ''
                }`}
                title={label}
              >
                <Icon size={16} weight={active ? 'fill' : 'regular'} />
                {/* Подпись остаётся в разметке и прячется стилями: сайдбар
                    сложен до иконок, но что это за пункты — видно в вёрстке и
                    в поиске по странице. */}
                <span className="panel-mock__nav-label">{label}</span>
              </span>
              );
            })}
          </nav>

          <div className="panel-mock__content">
            <div className="panel-mock__page-head">
              <h3 className="panel-mock__page-title">Chats</h3>
              <Switch
                checked={aiMode}
                onChange={setAiMode}
                label={t('showcase.chats.aiMode', 'AI mode')}
                className="panel-mock__ai-mode"
              />
            </div>

            <div
              className={`panel-inbox${aiMode ? '' : ' panel-inbox--replying'}`}
            >
              <ul className="panel-inbox__list">
                {rows.map((c) => {
                  const Icon = CHANNEL_ICON[c.channel] ?? Globe;
                  const selected = c.id === active?.id;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        className={`panel-inbox__item${
                          selected ? ' panel-inbox__item--active' : ''
                        }${c.live ? ' panel-inbox__item--live' : ''}`}
                        onClick={() => setActiveId(c.id)}
                        aria-current={selected}
                      >
                        <span className="panel-inbox__item-head">
                          <Icon
                            size={16}
                            weight="fill"
                            className="panel-inbox__channel"
                            aria-hidden="true"
                          />
                          <span className="panel-inbox__item-name">
                            {c.live ? t('showcase.inbox.you', c.name) : c.name}
                          </span>
                          {c.live && (
                            <span className="panel-inbox__new">
                              {t('showcase.inbox.new', 'New')}
                            </span>
                          )}
                          <span className="panel-inbox__item-time">
                            {c.time}
                          </span>
                        </span>
                        <span className="panel-inbox__item-foot">
                          <span className="panel-inbox__item-preview">
                            {c.previewKey
                              ? t(`showcase.inbox.${c.previewKey}`, c.preview)
                              : c.preview}
                          </span>
                          {c.unread > 0 && (
                            <span
                              className="panel-inbox__unread"
                              aria-label={`${c.unread} ${t('showcase.inbox.unread', 'unread')}`}
                            >
                              {c.unread}
                            </span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              <div className="panel-inbox__conversation">
                <div className="panel-inbox__thread">
                  {active?.thread.map((m, i) => (
                    <div
                      key={i}
                      className={`panel-inbox__bubble panel-inbox__bubble--${m.from}`}
                    >
                      {/* Реплики фикстур — на языке витрины (#1431); живые
                          сообщения посетителя приходят как написаны. */}
                      {m.key ? t(`showcase.inbox.${m.key}`, m.text) : m.text}
                      {m.from === BOT_AUTHOR && (
                        <span
                          className="panel-inbox__bot"
                          role="img"
                          aria-label={t('showcase.chats.bot', 'Assistant')}
                          title={t('showcase.chats.bot', 'Assistant')}
                        >
                          <Robot size={14} weight="regular" aria-hidden="true" />
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Отвечать можно, только когда ассистент отключён: иначе на один вопрос
          отвечали бы двое сразу. Живой разговор идёт в чате слева — ответ
          команды приходит туда же. */}
                {!aiMode && (
                  <form
                    className="panel-inbox__reply"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (chatMirror.sendFromTeam(draft)) setDraft('');
                    }}
                  >
                    <Input
                      className="panel-inbox__reply-input"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      placeholder={t(
                        'showcase.chats.reply',
                        'Reply as the team'
                      )}
                      aria-label={t(
                        'showcase.chats.reply',
                        'Reply as the team'
                      )}
                    />
                    {/* Знаком, а не словом: в панели кнопка отправки стоит вплотную к
              полю и подписи не носит — слово «Send» рядом с плейсхолдером
              читалось второй подписью к нему. Доступное имя осталось: читалке
              одной стрелки мало. */}
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!draft.trim()}
                      aria-label={t('showcase.chats.send', 'Send')}
                      title={t('showcase.chats.send', 'Send')}
                      className="panel-inbox__send"
                    >
                      <ArrowUp size={18} weight="bold" aria-hidden="true" />
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MockWindow>
  );
};

PanelInbox.propTypes = {
  /** Разговоры инбокса. `channel` — website | whatsapp | telegram. */
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      channel: PropTypes.oneOf(['website', 'whatsapp', 'telegram']).isRequired,
      time: PropTypes.string,
      unread: PropTypes.number,
      preview: PropTypes.string,
      thread: PropTypes.arrayOf(
        PropTypes.shape({
          from: PropTypes.oneOf(['customer', 'agent', 'human']).isRequired,
          text: PropTypes.string.isRequired,
        })
      ).isRequired,
    })
  ),
};

export default PanelInbox;
